export const getCountries = (): string[] => {
  return [
    'Australia',
    'Austria',
    'Belgium',
    'Brazil',
    'Bulgaria',
    'Canada',
    'Croatia',
    'Cyprus',
    'Czech Republic',
    'Denmark',
    'Estonia',
    'Finland',
    'France',
    'Germany',
    'Gibraltar',
    'Greece',
    'Hong Kong',
    'Hungary',
    'India',
    'Indonesia',
    'Ireland',
    'Italy',
    'Japan',
    'Latvia',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Malaysia',
    'Malta',
    'Mexico',
    'Netherlands',
    'New Zealand',
    'Norway',
    'Poland',
    'Portugal',
    'Romania',
    'Singapore',
    'Slovakia',
    'Slovenia',
    'Spain',
    'Sweden',
    'Switzerland',
    'Thailand',
    'United Arab Emirates',
    'United Kingdom',
    'United States',
  ];
};

export enum CountryCurrencyEnum {
  Dollar = 'dollar',
  Euro = 'euro',
  Pound = 'pound',
}

export type CurrencySymbol = '$' | '€' | '£';

export const CountryCurrencySymbolEnum: Record<CountryCurrencyEnum, CurrencySymbol> = {
  [CountryCurrencyEnum.Dollar]: '$',
  [CountryCurrencyEnum.Euro]: '€',
  [CountryCurrencyEnum.Pound]: '£',
};

export interface PostalCodeFormat {
  country: string;
  code: string;
  example: string;
  mask?: string | string[];
  currency?: CountryCurrencyEnum;
}

export const postalCodeFormatMap: Record<string, PostalCodeFormat> = {
  Australia: {
    country: 'Australia',
    code: 'AU',
    example: '4000',
    mask: '9999',
    currency: CountryCurrencyEnum.Dollar,
  },
  Austria: {
    country: 'Austria',
    code: 'AT',
    example: '1010',
    mask: '9999',
    currency: CountryCurrencyEnum.Euro,
  },
  Belgium: {
    country: 'Belgium',
    code: 'BE',
    example: '1000',
    mask: '9999',
    currency: CountryCurrencyEnum.Euro,
  },
  Brazil: {
    country: 'Brazil',
    code: 'BR',
    example: '01000-000',
    mask: '99999-999',
    currency: CountryCurrencyEnum.Dollar, // BRL (Brazilian Real) - using Dollar as closest match
  },
  Bulgaria: {
    country: 'Bulgaria',
    code: 'BG',
    example: '1000',
    mask: '9999',
    currency: CountryCurrencyEnum.Euro, // BGN (Bulgarian Lev) - using Euro as closest match
  },
  Canada: {
    country: 'Canada',
    code: 'CA',
    example: 'K1A 0B1',
    mask: 'A9A 9A9',
    currency: CountryCurrencyEnum.Dollar,
  },
  Croatia: {
    country: 'Croatia',
    code: 'HR',
    example: '10000',
    mask: '99999',
    currency: CountryCurrencyEnum.Euro,
  },
  Cyprus: {
    country: 'Cyprus',
    code: 'CY',
    example: '2008',
    mask: '9999',
    currency: CountryCurrencyEnum.Euro,
  },
  'Czech Republic': {
    country: 'Czech Republic',
    code: 'CZ',
    example: '110 00',
    mask: '999 99',
    currency: CountryCurrencyEnum.Euro, // CZK (Czech Koruna) - using Euro as closest match
  },
  Denmark: {
    country: 'Denmark',
    code: 'DK',
    example: '2100',
    mask: '9999',
    currency: CountryCurrencyEnum.Euro, // DKK (Danish Krone) - using Euro as closest match
  },
  Estonia: {
    country: 'Estonia',
    code: 'EE',
    example: '10111',
    mask: '99999',
    currency: CountryCurrencyEnum.Euro,
  },
  Finland: {
    country: 'Finland',
    code: 'FI',
    example: '00100',
    mask: '99999',
    currency: CountryCurrencyEnum.Euro,
  },
  France: {
    country: 'France',
    code: 'FR',
    example: '75008',
    mask: '99999',
    currency: CountryCurrencyEnum.Euro,
  },
  Germany: {
    country: 'Germany',
    code: 'DE',
    example: '10115',
    mask: '99999',
    currency: CountryCurrencyEnum.Euro,
  },
  Gibraltar: {
    country: 'Gibraltar',
    code: 'GI',
    example: 'GX11 1AA',
    mask: 'AA99 9AA',
    currency: CountryCurrencyEnum.Pound,
  },
  Greece: {
    country: 'Greece',
    code: 'GR',
    example: '105 58',
    mask: '999 99',
    currency: CountryCurrencyEnum.Euro,
  },
  'Hong Kong': {
    country: 'Hong Kong',
    code: 'HK',
    example: '— (not used)',
    mask: '',
    currency: CountryCurrencyEnum.Dollar,
  },
  Hungary: {
    country: 'Hungary',
    code: 'HU',
    example: '1051',
    mask: '9999',
    currency: CountryCurrencyEnum.Euro, // HUF (Hungarian Forint) - using Euro as closest match
  },
  India: {
    country: 'India',
    code: 'IN',
    example: '110001',
    mask: '999999',
    currency: CountryCurrencyEnum.Dollar, // INR (Indian Rupee) - using Dollar as closest match
  },
  Indonesia: {
    country: 'Indonesia',
    code: 'ID',
    example: '10110',
    mask: '99999',
    currency: CountryCurrencyEnum.Dollar, // IDR (Indonesian Rupiah) - using Dollar as closest match
  },
  Ireland: {
    country: 'Ireland',
    code: 'IE',
    example: 'D02 XY76',
    mask: 'A99 AAA9',
    currency: CountryCurrencyEnum.Euro,
  },
  Italy: {
    country: 'Italy',
    code: 'IT',
    example: '00144',
    mask: '99999',
    currency: CountryCurrencyEnum.Euro,
  },
  Japan: {
    country: 'Japan',
    code: 'JP',
    example: '100-0001',
    mask: '999-9999',
    currency: CountryCurrencyEnum.Dollar, // JPY (Japanese Yen) - using Dollar as closest match
  },
  Latvia: {
    country: 'Latvia',
    code: 'LV',
    example: 'LV-1050',
    mask: 'LV-9999',
    currency: CountryCurrencyEnum.Euro,
  },
  Liechtenstein: {
    country: 'Liechtenstein',
    code: 'LI',
    example: '9490',
    mask: '9999',
    currency: CountryCurrencyEnum.Euro,
  },
  Lithuania: {
    country: 'Lithuania',
    code: 'LT',
    example: 'LT-01100',
    mask: 'LT-99999',
    currency: CountryCurrencyEnum.Euro,
  },
  Luxembourg: {
    country: 'Luxembourg',
    code: 'LU',
    example: 'L-1111',
    mask: 'L-9999',
    currency: CountryCurrencyEnum.Euro,
  },
  Malaysia: {
    country: 'Malaysia',
    code: 'MY',
    example: '50450',
    mask: '99999',
    currency: CountryCurrencyEnum.Dollar, // MYR (Malaysian Ringgit) - using Dollar as closest match
  },
  Malta: {
    country: 'Malta',
    code: 'MT',
    example: 'BKR 4013',
    mask: 'AAA 9999',
    currency: CountryCurrencyEnum.Euro,
  },
  Mexico: {
    country: 'Mexico',
    code: 'MX',
    example: '01000',
    mask: '99999',
    currency: CountryCurrencyEnum.Dollar,
  },
  Netherlands: {
    country: 'Netherlands',
    code: 'NL',
    example: '1234 AB',
    mask: '9999 AA',
    currency: CountryCurrencyEnum.Euro,
  },
  'New Zealand': {
    country: 'New Zealand',
    code: 'NZ',
    example: '6011',
    mask: '9999',
    currency: CountryCurrencyEnum.Dollar,
  },
  Norway: {
    country: 'Norway',
    code: 'NO',
    example: '0150',
    mask: '9999',
    currency: CountryCurrencyEnum.Euro, // NOK (Norwegian Krone) - using Euro as closest match
  },
  Poland: {
    country: 'Poland',
    code: 'PL',
    example: '90-224',
    mask: '99-999',
    currency: CountryCurrencyEnum.Euro, // PLN (Polish Złoty) - using Euro as closest match
  },
  Portugal: {
    country: 'Portugal',
    code: 'PT',
    example: '1000-001',
    mask: '9999-999',
    currency: CountryCurrencyEnum.Euro,
  },
  Romania: {
    country: 'Romania',
    code: 'RO',
    example: '010011',
    mask: '999999',
    currency: CountryCurrencyEnum.Euro, // RON (Romanian Leu) - using Euro as closest match
  },
  Singapore: {
    country: 'Singapore',
    code: 'SG',
    example: '238801',
    mask: '999999',
    currency: CountryCurrencyEnum.Dollar,
  },
  Slovakia: {
    country: 'Slovakia',
    code: 'SK',
    example: '811 01',
    mask: '999 99',
    currency: CountryCurrencyEnum.Euro, // EUR (Euro) - Slovakia uses Euro
  },
  Slovenia: {
    country: 'Slovenia',
    code: 'SI',
    example: '1000',
    mask: '9999',
    currency: CountryCurrencyEnum.Euro, // EUR (Euro) - Slovenia uses Euro
  },
  Spain: {
    country: 'Spain',
    code: 'ES',
    example: '28013',
    mask: '99999',
    currency: CountryCurrencyEnum.Euro,
  },
  Sweden: {
    country: 'Sweden',
    code: 'SE',
    example: '114 55',
    mask: '999 99',
    currency: CountryCurrencyEnum.Euro, // SEK (Swedish Krona) - using Euro as closest match
  },
  Switzerland: {
    country: 'Switzerland',
    code: 'CH',
    example: '8000',
    mask: '9999',
    currency: CountryCurrencyEnum.Euro, // CHF (Swiss Franc) - using Euro as closest match
  },
  Thailand: {
    country: 'Thailand',
    code: 'TH',
    example: '10110',
    mask: '99999',
    currency: CountryCurrencyEnum.Dollar, // THB (Thai Baht) - using Dollar as closest match
  },
  'United Arab Emirates': {
    country: 'United Arab Emirates',
    code: 'AE',
    example: '00000',
    mask: '99999',
    currency: CountryCurrencyEnum.Dollar, // AED (UAE Dirham) - using Dollar as closest match
  },
  'United Kingdom': {
    country: 'United Kingdom',
    code: 'GB',
    example: 'SW1A 1AA',
    mask: '',
    currency: CountryCurrencyEnum.Pound,
  },
  'United States': {
    country: 'United States',
    code: 'US',
    example: '90210 or 90210-1234',
    mask: ['99999', '99999-9999'],
    currency: CountryCurrencyEnum.Dollar,
  },
};
